import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { useToast } from './ui/Toast';
import './AccountDeletionManagement.css';
import {
  Trash2, Eye, X, Clock, CheckCircle, XCircle,
  AlertTriangle, User, Store, Calendar, Mail,
  Shield, RefreshCw, ChevronDown, Search,
  FileText, DollarSign, Package
} from 'lucide-react';

const AccountDeletionManagement = () => {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/account-deletion-requests?status=${statusFilter}&page=${pagination.page}&limit=${pagination.limit}`);
      setRequests(response.requests || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 1
      }));
    } catch (err) {
      console.error('Error fetching deletion requests:', err);
      toast.error('Failed to load deletion requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pagination.page, pagination.limit, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/account-deletion-requests/stats');
      setStats(response.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchRequests();
  }, [fetchStats, fetchRequests]);

  const handleViewDetails = async (request) => {
    try {
      const response = await api.get(`/admin/account-deletion-requests/${request.id}`);
      setSelectedRequest(response.request);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching request details:', err);
      toast.error('Failed to load request details');
    }
  };

  const handleApprove = async (request) => {
    if (!window.confirm(`Are you sure you want to approve this deletion request?\n\nThis will permanently delete the merchant's account and all associated data.`)) {
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/admin/account-deletion-requests/${request.id}/approve`);
      toast.success('Deletion request approved');
      fetchRequests();
      fetchStats();
      setShowDetailModal(false);
    } catch (err) {
      console.error('Error approving request:', err);
      toast.error(err.error || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/admin/account-deletion-requests/${selectedRequest.id}/reject`, {
        reason: rejectReason
      });
      toast.success('Deletion request rejected');
      fetchRequests();
      fetchStats();
      setShowRejectModal(false);
      setShowDetailModal(false);
    } catch (err) {
      console.error('Error rejecting request:', err);
      toast.error(err.error || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      PENDING: { color: '#F59E0B', bg: '#FEF3C7', icon: Clock, label: 'Pending' },
      APPROVED: { color: '#3B82F6', bg: '#DBEAFE', icon: CheckCircle, label: 'Approved' },
      REJECTED: { color: '#EF4444', bg: '#FEE2E2', icon: XCircle, label: 'Rejected' },
      CANCELLED: { color: '#6B7280', bg: '#F3F4F6', icon: X, label: 'Cancelled' },
      COMPLETED: { color: '#10B981', bg: '#D1FAE5', icon: CheckCircle, label: 'Completed' }
    };
    const { color, bg, icon: Icon, label } = config[status] || config.PENDING;
    return (
      <span className="adm-status-badge" style={{ backgroundColor: bg, color }}>
        <Icon size={12} />
        {label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntilDeletion = (scheduledDate) => {
    if (!scheduledDate) return null;
    const days = Math.ceil((new Date(scheduledDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="adm-container">
      {/* Header */}
      <div className="adm-header">
        <div className="adm-header-left">
          <div className="adm-header-icon">
            <Trash2 size={24} />
          </div>
          <div>
            <h1>Account Deletion Requests</h1>
            <p>Manage merchant account deletion requests (GDPR compliance)</p>
          </div>
        </div>
        <button className="adm-refresh-btn" onClick={() => { fetchRequests(); fetchStats(); }}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="adm-stats-grid">
          <div className="adm-stat-card adm-stat-pending">
            <Clock size={20} />
            <div className="adm-stat-content">
              <span className="adm-stat-value">{stats.pending}</span>
              <span className="adm-stat-label">Pending</span>
            </div>
          </div>
          <div className="adm-stat-card adm-stat-approved">
            <CheckCircle size={20} />
            <div className="adm-stat-content">
              <span className="adm-stat-value">{stats.approved}</span>
              <span className="adm-stat-label">Approved</span>
            </div>
          </div>
          <div className="adm-stat-card adm-stat-rejected">
            <XCircle size={20} />
            <div className="adm-stat-content">
              <span className="adm-stat-value">{stats.rejected}</span>
              <span className="adm-stat-label">Rejected</span>
            </div>
          </div>
          <div className="adm-stat-card adm-stat-completed">
            <Shield size={20} />
            <div className="adm-stat-content">
              <span className="adm-stat-value">{stats.completed}</span>
              <span className="adm-stat-label">Completed</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="adm-filters">
        <div className="adm-filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="COMPLETED">Completed</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* Requests Table */}
      <div className="adm-table-container">
        {loading ? (
          <div className="adm-loading">
            <RefreshCw className="adm-spinner" size={24} />
            <span>Loading requests...</span>
          </div>
        ) : requests.length === 0 ? (
          <div className="adm-empty">
            <Trash2 size={48} />
            <h3>No deletion requests</h3>
            <p>No {statusFilter !== 'all' ? statusFilter.toLowerCase() : ''} account deletion requests found</p>
          </div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Email</th>
                <th>Requested</th>
                <th>Scheduled</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td>
                    <div className="adm-merchant-cell">
                      <div className="adm-merchant-avatar">
                        {request.user?.firstName?.[0] || request.user?.email?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="adm-merchant-info">
                        <span className="adm-merchant-name">
                          {request.user?.firstName} {request.user?.lastName || ''}
                        </span>
                        <span className="adm-merchant-tier">{request.user?.merchantTier || 'FREE'}</span>
                      </div>
                    </div>
                  </td>
                  <td>{request.email}</td>
                  <td>{formatDate(request.createdAt)}</td>
                  <td>
                    <div className="adm-scheduled-cell">
                      {formatDate(request.scheduledDeletionDate)}
                      {request.status === 'PENDING' && (
                        <span className="adm-days-badge">
                          {getDaysUntilDeletion(request.scheduledDeletionDate)} days left
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{getStatusBadge(request.status)}</td>
                  <td>
                    <div className="adm-actions">
                      <button
                        className="adm-action-btn adm-view-btn"
                        onClick={() => handleViewDetails(request)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      {request.status === 'PENDING' && (
                        <>
                          <button
                            className="adm-action-btn adm-approve-btn"
                            onClick={() => handleApprove(request)}
                            title="Approve"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            className="adm-action-btn adm-reject-btn"
                            onClick={() => handleOpenRejectModal(request)}
                            title="Reject"
                          >
                            <XCircle size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="adm-pagination">
          <button
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="adm-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h2>Deletion Request Details</h2>
              <button className="adm-modal-close" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="adm-modal-body">
              {/* Status Banner */}
              <div className={`adm-detail-status adm-detail-status-${selectedRequest.status.toLowerCase()}`}>
                {getStatusBadge(selectedRequest.status)}
                {selectedRequest.status === 'PENDING' && (
                  <span className="adm-detail-countdown">
                    {getDaysUntilDeletion(selectedRequest.scheduledDeletionDate)} days until scheduled deletion
                  </span>
                )}
              </div>

              {/* Merchant Info */}
              <div className="adm-detail-section">
                <h3><User size={16} /> Merchant Information</h3>
                <div className="adm-detail-grid">
                  <div className="adm-detail-item">
                    <label>Name</label>
                    <span>{selectedRequest.user?.firstName} {selectedRequest.user?.lastName}</span>
                  </div>
                  <div className="adm-detail-item">
                    <label>Email</label>
                    <span>{selectedRequest.email}</span>
                  </div>
                  <div className="adm-detail-item">
                    <label>Phone</label>
                    <span>{selectedRequest.user?.phone || 'N/A'}</span>
                  </div>
                  <div className="adm-detail-item">
                    <label>Tier</label>
                    <span>{selectedRequest.user?.merchantTier || 'FREE'}</span>
                  </div>
                  <div className="adm-detail-item">
                    <label>Member Since</label>
                    <span>{formatDate(selectedRequest.user?.createdAt)}</span>
                  </div>
                </div>
              </div>

              {/* Stores */}
              {selectedRequest.user?.stores?.length > 0 && (
                <div className="adm-detail-section">
                  <h3><Store size={16} /> Stores ({selectedRequest.user.stores.length})</h3>
                  <div className="adm-stores-list">
                    {selectedRequest.user.stores.map((store) => (
                      <div key={store.id} className="adm-store-item">
                        <div className="adm-store-info">
                          <span className="adm-store-name">{store.name}</span>
                          <span className="adm-store-slug">/{store.slug}</span>
                        </div>
                        <span className={`adm-store-status ${store.isLive ? 'adm-store-live' : 'adm-store-offline'}`}>
                          {store.isLive ? 'Live' : 'Offline'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              {selectedRequest.stats && (
                <div className="adm-detail-section">
                  <h3><FileText size={16} /> Account Statistics</h3>
                  <div className="adm-detail-stats">
                    <div className="adm-detail-stat">
                      <Package size={16} />
                      <span>{selectedRequest.stats.totalOrders} Orders</span>
                    </div>
                    <div className="adm-detail-stat">
                      <DollarSign size={16} />
                      <span>₦{selectedRequest.stats.totalRevenue?.toLocaleString() || 0} Revenue</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Details */}
              <div className="adm-detail-section">
                <h3><Calendar size={16} /> Request Details</h3>
                <div className="adm-detail-grid">
                  <div className="adm-detail-item">
                    <label>Requested At</label>
                    <span>{formatDate(selectedRequest.createdAt)}</span>
                  </div>
                  <div className="adm-detail-item">
                    <label>Scheduled Deletion</label>
                    <span>{formatDate(selectedRequest.scheduledDeletionDate)}</span>
                  </div>
                  {selectedRequest.reason && (
                    <div className="adm-detail-item adm-detail-full">
                      <label>Reason</label>
                      <span>{selectedRequest.reason}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rejection Info */}
              {selectedRequest.status === 'REJECTED' && selectedRequest.metadata?.rejectionReason && (
                <div className="adm-detail-section adm-detail-rejection">
                  <h3><AlertTriangle size={16} /> Rejection Details</h3>
                  <p>{selectedRequest.metadata.rejectionReason}</p>
                  <span className="adm-detail-meta">
                    Rejected by {selectedRequest.metadata.rejectedBy} on {formatDate(selectedRequest.metadata.rejectedAt)}
                  </span>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            {selectedRequest.status === 'PENDING' && (
              <div className="adm-modal-footer">
                <button
                  className="adm-btn adm-btn-secondary"
                  onClick={() => handleOpenRejectModal(selectedRequest)}
                  disabled={actionLoading}
                >
                  <XCircle size={16} />
                  Reject
                </button>
                <button
                  className="adm-btn adm-btn-danger"
                  onClick={() => handleApprove(selectedRequest)}
                  disabled={actionLoading}
                >
                  {actionLoading ? <RefreshCw className="adm-spinner" size={16} /> : <CheckCircle size={16} />}
                  Approve Deletion
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="adm-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="adm-modal adm-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h2>Reject Deletion Request</h2>
              <button className="adm-modal-close" onClick={() => setShowRejectModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="adm-modal-body">
              <p className="adm-reject-info">
                Please provide a reason for rejecting this deletion request.
                The merchant will be notified of your decision.
              </p>
              <div className="adm-form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter the reason for rejecting this request..."
                  rows={4}
                />
              </div>
            </div>
            <div className="adm-modal-footer">
              <button
                className="adm-btn adm-btn-secondary"
                onClick={() => setShowRejectModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="adm-btn adm-btn-danger"
                onClick={handleReject}
                disabled={actionLoading || !rejectReason.trim()}
              >
                {actionLoading ? <RefreshCw className="adm-spinner" size={16} /> : <XCircle size={16} />}
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDeletionManagement;
